import { useCallback, useContext, useEffect, useRef } from 'react'

import { PlayerContext } from '../context/PlayerContext'


const SEEK_STEP_MS = 10000

export const useVideoMethods = () => {
    const { video } = useContext(PlayerContext)

    const jumpBack = useCallback(() => video.controller?.seekBy(-SEEK_STEP_MS), [video])
    const jumpForward = useCallback(() => video.controller?.seekBy(+SEEK_STEP_MS), [video])
    const seekBy = useCallback((ms: number) => video.controller?.seekBy(ms), [video])
    const toggleMute = useCallback(() => video.controller?.toggleMute(), [video])
    const togglePause = useCallback(() => video.controller?.togglePause(), [video])
    const unpause = useCallback(() => video.controller?.unpause(), [video])
    const pause = useCallback(() => video.controller?.pause(), [video])
    const replay = useCallback(() => video.controller?.replay(), [video])
    const goFullScreen = useCallback(() => video.controller?.goFullScreen(), [video])
    const volumeUp = useCallback(() => video.controller?.volumeUp(), [video])
    const volumeDown = useCallback(() => video.controller?.volumeDown(), [video])
    const retry = useCallback(() => video.controller?.retry(), [video])
    const close = useCallback(() => video.controller?.close(), [video])

    return {
        jumpBack,
        jumpForward,
        seekBy,
        toggleMute,
        togglePause,
        unpause,
        pause,
        replay,
        goFullScreen,
        volumeUp,
        volumeDown,
        retry,
        close,
    }
}

interface Callbacks {
    onStart?: () => void
    onEnd?: () => void
    onReplay?: () => void
    onLoadFailed?: () => void
    onProgress?: (ms: number) => void
}

// TODO TIV-1296 is this useful? I think it was originally intended for analytics
export const useMainEvents = (callbacks: Callbacks) => {
    const context = useContext(PlayerContext)

    const lastTimeUpdate = useRef(0)
    const STEP_MS = 1000 * 60 * 5 // 5 mins

    useEffect(() => {
        // TODO this gets triggered event if video load fails
        const onPlay = () => {
            callbacks.onStart?.()
        }

        const onTimeUpdate = (ms: number) => {
            if (lastTimeUpdate.current + STEP_MS < ms) {
                lastTimeUpdate.current = ms
                callbacks.onProgress?.(ms)
            }
        }

        const onLoadFailed = () => {
            callbacks.onLoadFailed?.()
        }

        context.video.engine?.addEventListener('play', onPlay)
        context.video.engine?.addEventListener('timeupdate', onTimeUpdate)
        context.video.engine?.addEventListener('loadfailed', onLoadFailed)

        return () => {
            context.video.engine?.removeEventListener('play', onPlay)
            context.video.engine?.removeEventListener('timeupdate', onTimeUpdate)
            context.video.engine?.removeEventListener('loadfailed', onLoadFailed)
        }
    }, [])
}
